import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { ISRAELI_BANKS, bankOptionLabel } from '../../data/israeliBanks';

type BankSelectProps = {
  value: string;
  onChange: (value: string) => void;
  valueMode?: 'name' | 'code';
  required?: boolean;
  allowOther?: boolean;
  className?: string;
};

const normalize = (value: string) => value.trim().toLocaleLowerCase('he');
const comparableCode = (value: string) => String(Number(value));

export default function BankSelect({
  value,
  onChange,
  valueMode = 'name',
  required = false,
  allowOther = valueMode === 'name',
  className = '',
}: BankSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
    else setQuery('');
  }, [open]);

  const selected = ISRAELI_BANKS.find(bank =>
    valueMode === 'code'
      ? comparableCode(bank.code) === comparableCode(value)
      : bank.name === value,
  );

  const filtered = useMemo(() => {
    const needle = normalize(query);
    if (!needle) return ISRAELI_BANKS;
    return ISRAELI_BANKS.filter(bank =>
      normalize(`${bank.code} ${comparableCode(bank.code)} ${bank.name}`).includes(needle),
    );
  }, [query]);

  const choose = (bank: (typeof ISRAELI_BANKS)[number] | 'other') => {
    if (bank === 'other') onChange('אחר');
    else onChange(valueMode === 'code' ? comparableCode(bank.code) : bank.name);
    setOpen(false);
  };

  const label = selected ? bankOptionLabel(selected) : value || 'בחר בנק…';

  return (
    <div ref={rootRef} className={`relative ${className}`} dir="rtl">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
        className="flex w-full items-center justify-between gap-3 rounded-lg border border-gray-300 bg-white px-3 py-2 text-right text-sm text-gray-900 shadow-sm transition hover:border-blue-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
      >
        <span className={value ? 'truncate' : 'truncate text-gray-400'}>{label}</span>
        <ChevronDown size={17} className={`shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {required && <input tabIndex={-1} aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 opacity-0" value={value} onChange={() => {}} required />}

      {open && (
        <div className="absolute z-[120] mt-2 w-full min-w-[280px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-600 dark:bg-gray-800">
          <div className="border-b border-gray-100 p-2 dark:border-gray-700">
            <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 dark:bg-gray-900/60">
              <Search size={16} className="shrink-0 text-gray-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={event => setQuery(event.target.value)}
                onKeyDown={event => event.key === 'Escape' && setOpen(false)}
                placeholder="חיפוש לפי שם או מספר בנק…"
                className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-gray-400 dark:text-white"
              />
            </div>
          </div>
          <div role="listbox" className="max-h-64 overflow-y-auto p-1.5">
            {filtered.map(bank => {
              const optionValue = valueMode === 'code' ? comparableCode(bank.code) : bank.name;
              const active = value === optionValue;
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  key={bank.code}
                  onClick={() => choose(bank)}
                  className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-right text-sm transition ${active ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'}`}
                >
                  <span className="inline-flex w-9 shrink-0 justify-center rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-xs font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">{bank.code}</span>
                  <span className="flex-1">{bank.name}</span>
                  {active && <Check size={16} className="shrink-0" />}
                </button>
              );
            })}
            {filtered.length === 0 && <p className="px-3 py-6 text-center text-sm text-gray-500">לא נמצאו בנקים</p>}
            {allowOther && (!query || 'אחר'.includes(query)) && (
              <button type="button" onClick={() => choose('other')} className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-right text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700">
                <span className="inline-flex w-9 shrink-0 justify-center rounded-md bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-700">—</span>
                <span className="flex-1">אחר</span>
                {value === 'אחר' && <Check size={16} />}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
